<template>
    <div v-if="shouldShowDropdown" class="impersonation-dropdown">
        <b-dropdown
            :text="impersonationText"
            toggle-class="text-decoration-none"
            variant="outline-secondary"
            size="sm"
            right
            :offset="0"
            :boundary="'viewport'"
            :flip="true">

            <!-- Impersonation Status Header -->
            <template v-if="isImpersonated">
                <b-dropdown-header>
                    <small class="text-muted">
                        Impersonating: {{ currentUser.name }} (ID: {{ currentUser.id }})
                    </small>
                    <br>
                    <small class="text-muted" v-if="impersonator">
                        Original user: {{ impersonator.name }} (ID: {{ impersonator.id }})
                    </small>
                </b-dropdown-header>
                <b-dropdown-divider></b-dropdown-divider>
            </template>

            <!-- Stop Impersonation -->
            <b-dropdown-item
                v-if="isImpersonated"
                @click="stopImpersonation"
                class="text-danger">
                Stop Impersonation
            </b-dropdown-item>

            <!-- Start Impersonation -->
            <template v-if="!isImpersonated">
                <b-dropdown-header>
                    <small class="text-muted">
                        Impersonate User
                    </small>
                </b-dropdown-header>
                <b-dropdown-divider></b-dropdown-divider>

                <!-- Search Field -->
                <b-dropdown-item class="search-item">
                    <div class="search-container">
                        <input
                            v-model="searchQuery"
                            type="text"
                            placeholder="Search users..."
                            class="search-input"
                            @click.stop>
                    </div>
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>

                <!-- Users List Container -->
                <div class="users-list-container">
                    <b-dropdown-item
                        v-for="user in filteredUsers"
                        :key="user.id"
                        @click="startImpersonation(user)"
                        :disabled="loading"
                        class="user-item">
                        <div class="user-info">
                            <div class="user-name">{{ user.name }} (ID: {{ user.id }})</div>
                            <small class="text-muted user-email">{{ user.email }}</small>
                        </div>
                    </b-dropdown-item>
                </div>

                <!-- Loading State -->
                <b-dropdown-item v-if="loading" disabled>
                    Loading...
                </b-dropdown-item>

                <!-- Empty State -->
                <b-dropdown-item v-if="!loading && filteredUsers.length === 0" disabled>
                    <small class="text-muted">
                        {{ searchQuery ? 'No users found matching your search' : 'No users available' }}
                    </small>
                </b-dropdown-item>
            </template>
        </b-dropdown>
    </div>
</template>

<script>
export default {
    name: 'ImpersonationDropdown',
    data() {
        return {
            users: [],
            loading: false,
            searchQuery: '',
            isSuperAdmin: false,
            isImpersonated: false,
            currentUser: null,
            impersonator: null
        }
    },
    computed: {
        shouldShowDropdown() {
            return this.isSuperAdmin || this.isImpersonated;
        },
        impersonationText() {
            if (this.isImpersonated) {
                return `Impersonating: ${this.currentUser.name}`;
            }
            return 'Impersonate';
        },
        filteredUsers() {
            if (!this.searchQuery) {
                return this.users;
            }

            const query = this.searchQuery.toLowerCase();
            return this.users.filter(user =>
                user.name.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query)
            );
        }
    },
    async mounted() {
        setTimeout(() => {
            this.loadComponentData();
        }, 5000);
    },
    methods: {
        async loadComponentData() {
            this.loading = true;
            try {
                // Use store token for API calls
                const token = this.$store.state.token;
                if (token) {
                    this.$http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                }

                // Get current user and impersonation status
                const statusResponse = await this.$http.get('/api/auth/impersonation/status');
                const statusData = statusResponse.data;

                this.currentUser = statusData.current_user;
                this.isImpersonated = statusData.is_impersonated;
                this.impersonator = statusData.impersonator;
                this.isSuperAdmin = statusData.current_user.is_super_admin == 1;

                // If impersonated, we're not acting as super admin
                if (this.isImpersonated) {
                    this.isSuperAdmin = false;
                }

                // Load users list if we're a super admin and not impersonating
                if (this.isSuperAdmin && !this.isImpersonated) {
                    await this.loadUsers();
                }

            } catch (error) {
                this.$bvToast.toast('Failed to load impersonation data', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });
            } finally {
                this.loading = false;
            }
        },

        async loadUsers() {
            try {
                // Use store token for API calls
                const token = this.$store.state.token;
                if (token) {
                    this.$http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                }

                const response = await this.$http.get('/api/auth/impersonation/users');
                this.users = response.data;
            } catch (error) {
                this.$bvToast.toast('Failed to load users', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });
            }
        },


        async startImpersonation(user) {
            this.loading = true;
            try {
                const response = await this.$http.post('/api/auth/impersonation/start', {
                    user_id: user.id
                });

                // Update localStorage and axios headers
                localStorage.setItem('token', response.data.token);
                this.$http.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

                this.$bvToast.toast(`Now impersonating ${user.name}`, {
                    title: 'Impersonation Started',
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });

                // Reload component data to reflect new state
                await this.loadComponentData();

                window.location.reload();

            } catch (error) {
                this.$bvToast.toast(error.response?.data?.error || 'Failed to start impersonation', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });
            } finally {
                this.loading = false;
            }
        },

        async stopImpersonation() {
            this.loading = true;
            try {
                const response = await this.$http.post('/api/auth/impersonation/stop');

                // Update localStorage and axios headers
                localStorage.setItem('token', response.data.token);
                this.$http.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

                this.$bvToast.toast('Stopped impersonation', {
                    title: 'Impersonation Stopped',
                    variant: 'info',
                    solid: true,
                    autoHideDelay: 3000
                });

                // Reload component data to reflect new state
                await this.loadComponentData();

                // Redirect to home page after stopping impersonation
                setTimeout(() => {
                    window.location.href = '/';
                }, 3000);

            } catch (error) {
                this.$bvToast.toast(error.response?.data?.error || 'Failed to stop impersonation', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.impersonation-dropdown {
    margin-left: 10px;
    display: flex;
    align-items: center;
}

.impersonation-dropdown .dropdown-toggle {
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
    display: flex;
    align-items: center;
    height: auto;
    line-height: 1.2;
}

.impersonation-dropdown .dropdown-item {
    padding: 0.5rem 1rem;
}

.impersonation-dropdown .dropdown-item small {
    font-size: 0.75rem;
}

.impersonation-dropdown .dropdown-header {
    padding: 0.5rem 1rem;
    font-weight: 600;
}


.search-item {
    padding: 0 !important;
}

.search-container {
    position: relative;
    padding: 0.5rem 1rem;
}


.search-input {
    width: 100%;
    padding: 0.375rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    background-color: #fff;
    outline: none;
}

.search-input:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.users-list-container {
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
}

.users-list-container::-webkit-scrollbar {
    width: 6px;
}

.users-list-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.users-list-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.users-list-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.user-item {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #f8f9fa;
}

.user-item:last-child {
    border-bottom: none;
}

.user-item:hover {
    background-color: #f8f9fa;
}

.user-info {
    flex: 1;
    margin-left: 0.5rem;
}

.user-name {
    font-weight: 500;
    color: #212529;
    margin-bottom: 0.125rem;
}

.user-email {
    font-size: 0.75rem;
    color: #6c757d;
    line-height: 1.2;
}

.impersonation-dropdown .dropdown-menu {
    min-width: 280px;
    max-width: 350px;
    border-radius: 0.375rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
    .impersonation-dropdown .dropdown-menu {
        min-width: 250px;
        max-width: 300px;
    }

    .users-list-container {
        max-height: 250px;
    }
}
</style>
